import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import useMidnightCountdown, { formatCountdownInline } from "../../hooks/useMidnightCountdown";
import { formatFeaturedLongDate } from "../../utils/featuredDisplay";
import { loadFeaturedLandingData } from "../../services/featuredService";
import PastFeaturedStrip from "../../components/featured/PastFeaturedStrip";
import { CategoryAvatarPreview } from "../../components/featured/FeaturedCreatorCards";
import { formatCount } from "../../utils/formatCount";

import "./FeaturedLanding.css";

function IconSearch() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconStar() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(196,121,10,0.8)" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

export default function FeaturedLanding() {
  const navigate = useNavigate();
  const remainingMs = useMidnightCountdown();
  const [loading, setLoading] = useState(true);
  const [discoveryPreview, setDiscoveryPreview] = useState([]);
  const [communityPreview, setCommunityPreview] = useState([]);
  const [history, setHistory] = useState([]);
  const [nominationTotal, setNominationTotal] = useState(0);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        const result = await loadFeaturedLandingData();
        if (!cancelled) {
          setDiscoveryPreview(result.discovery);
          setCommunityPreview(result.community);
          setHistory(result.history);
          setNominationTotal(result.nominationTotal);
        }
      } catch (err) {
        console.error("Featured landing load error:", err);
        if (!cancelled) setLoadError(err.message || "Failed to load featured data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const cards = document.querySelectorAll(".ft-cat-card");
    const handlers = [];

    cards.forEach((card) => {
      const onMove = (e) => {
        const rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        card.style.transform = `translateY(-6px) rotateX(${-y * 4}deg) rotateY(${x * 4}deg)`;
      };
      const onLeave = () => {
        card.style.transform = "";
      };
      card.addEventListener("mousemove", onMove);
      card.addEventListener("mouseleave", onLeave);
      handlers.push({ card, onMove, onLeave });
    });

    return () => {
      handlers.forEach(({ card, onMove, onLeave }) => {
        card.removeEventListener("mousemove", onMove);
        card.removeEventListener("mouseleave", onLeave);
      });
    };
  }, [loading]);

  const todayLabel = formatFeaturedLongDate(new Date());

  return (
    <div className="ft-landing">
      <div className="ft-landing-bg" aria-hidden>
        <div className="ft-landing-bg-grain-wrap">
          <div className="ft-landing-bg-grain" />
        </div>
        <div className="ft-landing-bg-dots" />
        <div className="ft-landing-bg-glow-left" />
        <div className="ft-landing-bg-glow-right" />
      </div>

      <section className="ft-landing-hero">
        <div className="ft-landing-date">
          <span className="ft-landing-date-dot" />
          <span className="ft-landing-date-text">
            {todayLabel} · Renewed at midnight
          </span>
        </div>

        <div className="ft-landing-content">
          <span className="ft-landing-eyebrow">✦ Every night at midnight ✦</span>
          <h1 className="ft-landing-headline">
            Congratulations to today&apos;s
            <br />
            <em>featured creators</em>
          </h1>
          <p className="ft-landing-sub">
            10 Canadian creators. Two ways to be chosen. One night in the spotlight.
          </p>
          <span className="ft-landing-hand">— a celebration of the people building something real</span>

          {loadError && (
            <p className="ft-landing-load-error" role="alert">
              {loadError}
            </p>
          )}

          <div className="ft-category-cards">
            <button
              type="button"
              className="ft-cat-card ft-cat-card--discovery"
              onClick={() => navigate("/featured/discovery")}
            >
              <div className="ft-cat-icon ft-cat-icon--discovery">
                <IconSearch />
              </div>
              <span className="ft-cat-label">Chosen by chance</span>
              <span className="ft-cat-badge ft-cat-badge--discovery">🌱 Emerging voices · Under 10K</span>
              <h2 className="ft-cat-title">
                Tonight&apos;s <em>Discovery</em>
              </h2>
              <p className="ft-cat-desc">
                5 creators chosen at pure random. No nominations, no follower count advantage.
                Just the platform finding voices that deserve to be heard.
              </p>
              <CategoryAvatarPreview
                creators={discoveryPreview}
                extraLabel={
                  discoveryPreview.length
                    ? `${discoveryPreview.length} creators tonight`
                    : loading
                      ? "Loading…"
                      : "5 creators tonight"
                }
              />
              <div className="ft-cat-cta">
                <span className="ft-cat-btn ft-cat-btn--discovery">
                  See tonight&apos;s discoveries
                  <span className="ft-cat-arrow">→</span>
                </span>
              </div>
            </button>

            <button
              type="button"
              className="ft-cat-card ft-cat-card--community"
              onClick={() => navigate("/featured/community")}
            >
              <div className="ft-cat-icon ft-cat-icon--community">
                <IconStar />
              </div>
              <span className="ft-cat-label ft-cat-label--community">Chosen by the community</span>
              <span className="ft-cat-badge ft-cat-badge--community">✦ Community nominated</span>
              <h2 className="ft-cat-title">
                Community&apos;s <em>Choice</em>
              </h2>
              <p className="ft-cat-desc">
                5 creators chosen by a weighted lottery using fan nominations. Fans spent credits
                to nominate — this is who they believed in most.
              </p>
              <CategoryAvatarPreview
                creators={communityPreview}
                extraLabel={(() => {
                  if (nominationTotal > 0) {
                    return `${formatCount(nominationTotal)} nominations cast`;
                  }
                  if (communityPreview.length) {
                    return `${communityPreview.length} creators tonight`;
                  }
                  return loading ? "Loading…" : "5 creators tonight";
                })()}
              />
              <div className="ft-cat-cta">
                <span className="ft-cat-btn ft-cat-btn--community">
                  See who was chosen
                  <span className="ft-cat-arrow">→</span>
                </span>
              </div>
            </button>
          </div>

          <div className="ft-landing-footer">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            New creators featured every night at midnight EST ·{" "}
            <span className="ft-landing-countdown">
              Next rotation in {formatCountdownInline(remainingMs)}
            </span>
          </div>
        </div>

        <div className="ft-scroll-hint" aria-hidden>
          <span className="ft-scroll-hint-text">scroll</span>
          <div className="ft-scroll-line" />
        </div>
      </section>

      <section className="ft-below-fold">
        <div className="ft-bf-eyebrow">Archive</div>
        <h2 className="ft-bf-title">
          Previously <em>featured</em>
        </h2>
        <p className="ft-bf-sub">
          Every creator who has held a spotlight — discovered or chosen — lives here.
        </p>

        {loading ? (
          <p className="ft-past-loading">Loading featured history…</p>
        ) : (
          <PastFeaturedStrip items={history} />
        )}

        <div className="ft-how-row">
          <div className="ft-how-item">
            <div className="ft-how-num">01</div>
            <div className="ft-how-title">Discovery picks — pure random</div>
            <p className="ft-how-desc">
              5 spots chosen at random every midnight. Any creator with an open drop and under 10K
              followers is eligible. No nominations needed — just show up and create.
            </p>
            <span className="ft-how-hand">equal chance for every emerging voice</span>
          </div>
          <div className="ft-how-item">
            <div className="ft-how-num">02</div>
            <div className="ft-how-title">Community picks — fan nominated</div>
            <p className="ft-how-desc">
              5 spots chosen by weighted lottery using fan nominations. 50 credits per nomination,
              max 3 per fan per month. More nominations = higher chance, never a guarantee.
            </p>
            <span className="ft-how-hand">your credits, your voice, your choice</span>
          </div>
          <div className="ft-how-item">
            <div className="ft-how-num">03</div>
            <div className="ft-how-title">30 day cool-down for all</div>
            <p className="ft-how-desc">
              Once featured — whether discovered or chosen — a creator is locked out for 30 days.
              This keeps the spotlight fresh and gives every creator a fair shot at their turn.
            </p>
            <span className="ft-how-hand">a real honour, not a recurring slot</span>
          </div>
        </div>

        <div className="ft-below-links">
          <Link to="/featured/discovery" className="ft-below-link">
            Tonight&apos;s Discovery →
          </Link>
          <Link to="/featured/community" className="ft-below-link ft-below-link--gold">
            Community&apos;s Choice →
          </Link>
        </div>
      </section>
    </div>
  );
}
